import { AfterViewInit, ChangeDetectorRef, Component, forwardRef, Input, OnDestroy, ViewChild } from '@angular/core';
import {
  CountryISO,
  PhoneNumberFormat,
  SearchCountryField,
} from "ngx-intl-telephone-input";

import { PhoneNumberUtil } from 'google-libphonenumber';

import { Subject, takeUntil } from 'rxjs';
import { FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ThemeLayoutService } from 'src/app/@theme/services/theme-layout.service';
import { RTL } from 'src/app/@theme/const';
import { TabConst } from 'src/app/tabms/tab-consts';
import { BaseService } from 'src/app/tabms/services/base/base.service';

@Component({
  selector: 'app-base-form-phone-input',
  templateUrl: './base-form-phone-input.component.html',
  styleUrls: ['./base-form-phone-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BaseFormPhoneInputComponent),
      multi: true,
    }
  ]
})
export class BaseFormPhoneInputComponent implements AfterViewInit, OnDestroy {
  @Input() control: FormControl
  @Input() customErrorStyle: string
  @Input() size: 'default' | 'compact' | 'xs' = TabConst.DEFAULTSIZE // New input for size control

  @ViewChild('phoneInput', { static: true }) phoneInput!: any;

  type = 'phone'
// selectedCountryISO: CountryISO = CountryISO.SaudiArabia;
  CountryISO: any;
  PhoneNumberFormat: any;
  SearchCountryField: any;
  error
  currentLang: string
  selectedCountryISO: CountryISO | null = CountryISO.SaudiArabia;

  private destroy$: Subject<void> = new Subject<void>();
  private phoneUtil = PhoneNumberUtil.getInstance();

  constructor(
    private baseService: BaseService,
    private cdRef: ChangeDetectorRef,
    private themeLayoutService: ThemeLayoutService
  ) {
    this.CountryISO = CountryISO;
    this.PhoneNumberFormat = PhoneNumberFormat;
    this.SearchCountryField = SearchCountryField;
    this.currentLang = this.baseService.getCurrentLanguage();
    document.body.classList.add(this.currentLang);
    this.themeLayoutService.directionChange.pipe(takeUntil(this.destroy$)).subscribe((dir) => {
      if (dir === RTL) {
        this.currentLang = 'ar'
        document.body.classList.remove('en');
        document.body.classList.add(this.currentLang);
      }
      else {
        this.currentLang = 'en';
        document.body.classList.remove('ar');
        document.body.classList.add(this.currentLang);
      }
    })
  }

  ngAfterViewInit() {
    console.log('control', this.control);
    // Defer initial value sync to next tick to avoid NG0100 expression-changed errors
    setTimeout(() => {
      this.applyValueToPhoneComponent(this.control?.value ?? '');
      this.cdRef.detectChanges();
    });

    this.control?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe({
      next: (value) => {
        this.getErrorClass();
        this.applyValueToPhoneComponent(value);
        this.cdRef.detectChanges();
      }
    });
  }

  private applyValueToPhoneComponent(value: any) {
    const v = typeof value === 'string' ? value : (value?.e164Number || value?.internationalNumber || value?.number);
    if (!v) {
      // Empty value: clear number and set default country to Saudi Arabia
      this.selectedCountryISO = CountryISO.SaudiArabia;
      if (this.phoneInput) {
        this.phoneInput.selectedCountryISO = this.selectedCountryISO;
        try { this.phoneInput.setDefaultSelectedCountry(); } catch {}
        this.phoneInput.phoneNumber = '';
        // allow library to prefill dial code on focus
        this.phoneInput.isFocused = false;
      }
      return;
    }
    // Non-empty: set country from value then set number
    this.setSelectedCountryFromValue(v);
    if (this.phoneInput) {
      if (this.phoneInput.phoneNumber !== v) {
        this.phoneInput.phoneNumber = v;
      }
      // Trigger formatting + auto-country select inside library
      try {
        this.phoneInput.phoneValueChnaged(v);
      } catch {}
      // Avoid library clearing the number on first focus for prefilled values
      this.phoneInput.isFocused = true;
    }
  }

  onInputChange(event: any) {
    this.control.markAsTouched();
    // Determine the new value (string) and normalize
    const rawVal = (event?.phoneNumber ?? this.phoneInput?.phoneNumber ?? '') as string;
    const newVal = typeof rawVal === 'string' ? rawVal.trim() : '';

    if (newVal === '') {
      // Empty value: clear control and default to Saudi Arabia flag
      this.control.setValue('');
      this.control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      const errs = this.control.errors || {};
      delete errs['pattern'];
      this.control.setErrors(Object.keys(errs).length ? errs : null);

      this.selectedCountryISO = CountryISO.SaudiArabia;
      if (this.phoneInput) {
        this.phoneInput.selectedCountryISO = this.selectedCountryISO;
        try { this.phoneInput.setDefaultSelectedCountry(); } catch {}
        this.phoneInput.phoneNumber = '';
        this.phoneInput.isFocused = false;
      }
      this.getErrorClass();
      return;
    }

    // Non-empty: sync reactive control and validation
    this.control.setValue(newVal, { emitEvent: false });
    this.control.updateValueAndValidity({ onlySelf: true, emitEvent: false });

    const currentErrors = this.control.errors || {};
    if (!event.isNumberValid) {
      currentErrors['pattern'] = true;
      this.control.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
    } else {
      delete currentErrors['pattern'];
    }

    // Sync selected flag from event iso2Code if available; otherwise derive from value
    if (event?.iso2Code) {
      try {
        const region = String(event.iso2Code).toLowerCase();
        this.selectedCountryISO = region as CountryISO;
        if (this.phoneInput) {
          this.phoneInput.selectedCountryISO = this.selectedCountryISO;
          this.phoneInput.setDefaultSelectedCountry();
        }
      } catch { }
    } else {
      this.setSelectedCountryFromValue(newVal);
    }

    this.getErrorClass();
  }

  private setSelectedCountryFromValue(value: any) {
    try {
      if (!value) return;
      const v = typeof value === 'string' ? value : (value?.e164Number || value?.internationalNumber || value?.number);
      if (!v) return;
      const parsed = this.phoneUtil.parse(v);
      const region = this.phoneUtil.getRegionCodeForNumber(parsed); // e.g., 'EG'
      if (region) {
        const iso2 = region.toLowerCase();
        this.selectedCountryISO = iso2 as CountryISO;
        if (this.phoneInput) {
          this.phoneInput.selectedCountryISO = this.selectedCountryISO;
          this.phoneInput.setDefaultSelectedCountry();
        }
      }
    } catch {
      // ignore parse errors
    }
  }

  getErrorClass() {
    // Defer to next tick, and guard when component destroyed or input not present
    setTimeout(() => {
      if (!this.control) return;

      // Scope error styling to this component's own input element
      let inputEl: HTMLElement | null = null;
      try {
        const hostEl: HTMLElement | null = (this.phoneInput as any)?.elRef?.nativeElement
          || (this.phoneInput as any)?.elementRef?.nativeElement
          || null;
        if (hostEl) {
          inputEl = hostEl.querySelector('input[type="tel"]');
        }
      } catch {
        inputEl = null;
      }

      if (!inputEl) return;

      const isPristineOrValid = (this.control.untouched === true) || (this.control.valid === true);
      if (isPristineOrValid) {
        inputEl.classList.remove('inValid');
        inputEl.classList.remove('ng-touched');
        inputEl.classList.remove('ng-dirty');
      } else {
        inputEl.classList.add('inValid');
        inputEl.classList.add('ng-touched');
        inputEl.classList.add('ng-dirty');
      }
    });
     
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
